import type { FC } from 'react';
import { useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Host, VisitStatus } from 'store/visits/types';
import { useDateTimeFormatters } from 'shared/hooks/use-date-time-formatters/use-date-time-formatters.hook';
import { useEditVisitAction } from 'hooks/use-edit-visit-action.hook';
import { VisitStatusBadge } from 'components/shared/visit-status-badge';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@hqo/ui-atoms';
import { Edit2, MoreVertical, Repeat, XCircle } from 'lucide-react';
import { VisitorFrequencyEnum } from 'components/visit-form/types';
import { useFieldMetadata, useSuiteEnabled } from 'store/ui-metadata/hooks';
import { buildCancelVisitPath } from 'utils/route-helpers';
import { canCancelVisit, canEditVisit } from 'utils/visit-status-rules';
import type { OwnerParams } from 'shared/types/owner-params';

interface VisitorsListV2TableBodyRowProps {
  id: string;
  first_name: string;
  last_name: string;
  visitor_company_name?: string;
  arrival_date: Date;
  departure_date: Date;
  host: Host;
  floor: string;
  suite?: string;
  status: VisitStatus;
  visit_summary?: string;
  showVisitSummary: boolean;
  showFloor: boolean;
  showVisitorCompany: boolean;
  frequency?: VisitorFrequencyEnum;
  vendor_company_uuid?: string | null;
  is_group_visit?: boolean;
}
export const VisitorsListV2TableBodyRow: FC<VisitorsListV2TableBodyRowProps> = ({
  id,
  first_name,
  last_name,
  arrival_date,
  departure_date,
  host,
  floor,
  suite,
  status,
  visit_summary,
  showVisitSummary,
  showFloor,
  showVisitorCompany,
  visitor_company_name,
  frequency,
  vendor_company_uuid,
  is_group_visit,
}) => {
  const { formatMessage } = useIntl();
  const ownerParams = useParams<OwnerParams>();
  const navigate = useNavigate();
  const { formatDate, formatTimeRange } = useDateTimeFormatters();
  const handleEdit = useEditVisitAction(id);
  const handleCancelConfirm = useCallback(() => {
    navigate(buildCancelVisitPath(ownerParams as OwnerParams, id));
  }, [navigate, ownerParams, id]);

  const cancelVisitsEnabled = useFieldMetadata('cancel_visits_enabled');
  const editVisitsEnabled = useFieldMetadata('visit_editing_enabled');
  const suiteEnabled = useSuiteEnabled();
  const isVendorOrGroupVisit = Boolean(vendor_company_uuid) || Boolean(is_group_visit);
  // A finished visit offers nothing: the same rule the detail view applies, so the row and the page it
  // opens cannot disagree about what is still possible.
  const showEditAction = editVisitsEnabled && !isVendorOrGroupVisit;
  const canEdit = showEditAction && canEditVisit(status);
  const canCancel = cancelVisitsEnabled && canCancelVisit(status);

  const showSuite = suite && suiteEnabled;

  return (
    <tr key={id} className="hover:bg-gray-50/50 transition-colors">
      <td className="px-4 py-4 text-sm font-medium text-gray-900" data-testid="visitor-name">
        <div className="flex flex-col">
          {/*
            Every status opens, including cancelled. It was unclickable only because the one detail route
            was the post-create confirmation screen, which had nothing truthful to say about a visit that
            was not freshly confirmed.

            Bare `id`, no `../`: the rows render inside the pathless layout route, which already sits at
            `visits`. Climbing from there lands outside `visits/*` and bounces back to the list.
          */}
          <Link
            to={id}
            className="text-gray-900 hover:text-blue-600 hover:underline transition-colors cursor-pointer"
            data-testid="visitor-name-link"
            data-cy="visitor-name-link"
          >
            {first_name} {last_name}
          </Link>
          {showVisitorCompany && visitor_company_name && (
            <span className="text-gray-600 text-xs">{visitor_company_name}</span>
          )}
        </div>
      </td>
      <td className="px-4 py-4 text-sm text-gray-900" data-testid="visitor-date">
        <div className="flex items-start gap-0">
          <div className="flex flex-col">
            <div className="flex items-center gap-1">
              <span className="text-gray-900">{formatDate(arrival_date)}</span>
              {frequency && frequency !== VisitorFrequencyEnum.ONCE && (
                <Repeat className="h-4 w-4 text-gray-500 flex-shrink-0" />
              )}
            </div>
            {/* {/* {visit.startTime ? ( */}
            <span className="text-gray-600 text-xs">{formatTimeRange(arrival_date, departure_date)}</span>
            {/* ) : (
              <span className="text-gray-600 text-xs">All day</span>
            )} */}
          </div>
        </div>
      </td>
      {showVisitSummary && (
        <td className="px-4 py-4 text-sm text-gray-900" data-testid="visitor-summary">
          {visit_summary || '–'}
        </td>
      )}
      <td className="px-4 py-4 text-sm text-gray-600" data-testid="visitor-host">
        <div className="flex flex-col">
          {host ? (
            <span className="text-gray-600">{`${host.first_name} ${host.last_name}`}</span>
          ) : (
            <span className="text-gray-600">–</span>
          )}
          {host?.company_name && <span className="text-gray-600 text-xs">{host.company_name}</span>}
        </div>
      </td>
      {showFloor && (
        <td className="px-4 py-4 text-sm text-gray-900" data-testid="visitor-floor">
          {floor ? (
            <div className="flex flex-col">
              <span className="text-gray-900">
                <FormattedMessage id="visitsListV2.table.floor" values={{ floor }} />
              </span>
              {showSuite && (
                <span className="text-gray-600 text-xs">
                  <FormattedMessage id="visitsListV2.table.suite" values={{ suite }} />
                </span>
              )}
            </div>
          ) : (
            '–'
          )}
        </td>
      )}
      <td className="px-4 py-4" data-testid="visitor-status">
        <VisitStatusBadge status={status} />
      </td>
      <td className="px-4 py-4 text-right">
        {(canEdit || canCancel) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild data-cy={`dropdown-menu-trigger-${id}`}>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                aria-label={formatMessage(
                  { id: 'visitsListV2.table.actionsForVisitor' },
                  { visitorName: `${first_name} ${last_name}`.trim() },
                )}
              >
                <MoreVertical className="h-4 w-4" aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {canEdit && (
                <DropdownMenuItem onClick={handleEdit} data-testid="edit-visit">
                  <Edit2 className="h-4 w-4 mr-2" />
                  <FormattedMessage id="buttons.edit" />
                </DropdownMenuItem>
              )}
              {canCancel && (
                <DropdownMenuItem
                  onClick={handleCancelConfirm}
                  className="text-destructive focus:text-destructive"
                  data-testid="cancel-visit"
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  <FormattedMessage id="buttons.cancel" />
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </td>
    </tr>
  );
};
